import clsx from 'clsx'
import { type FactoryOpts } from 'imask'
import { useEffect } from 'react'
import { useIMask } from 'react-imask'
import { useControlField, useField } from 'remix-validated-form'
import { Input } from '~/components/ui'
import FieldContainer from './FieldContainer'

export enum NumberMaskType {
  integer = 'integer',
  year = 'year',
  decimal = 'decimal',
  acres = 'acres',
  currency = 'currency',
  percentage = 'percentage',
  coordinate = 'coordinate',
}

const NUMBER_MASK_OPTIONS: {
  [key in keyof typeof NumberMaskType]: FactoryOpts
} = {
  integer: {
    mask: Number,
    thousandsSeparator: ',',
    min: 0,
  },
  year: {
    mask: '0000',
    min: 0,
  },
  decimal: {
    mask: Number,
    thousandsSeparator: ',',
    scale: 2,
    min: 0,
    radix: '.',
  },
  acres: {
    mask: Number,
    thousandsSeparator: ',',
    scale: 4,
    min: 0,
    radix: '.',
  },
  currency: {
    mask: Number,
    thousandsSeparator: ',',
    scale: 2,
    min: 0,
    radix: '.',
    padFractionalZeros: true,
  },
  percentage: {
    mask: Number,
    scale: 2,
    min: 0,
    max: 100,
    radix: '.',
  },
  coordinate: {
    mask: Number,
    scale: 7,
    radix: '.',
    min: -180,
  },
}

type NumberFieldProps = {
  className?: string
  label?: string
  name: string
  required?: boolean
  disabled?: boolean
  maskType?: keyof typeof NumberMaskType
  prefix?: string
  suffix?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void
  description?: string
}

export default function NumberField(props: NumberFieldProps) {
  const {
    name,
    disabled,
    maskType = 'integer',
    prefix,
    suffix,
    onBlur: customOnBlur,
    description,
  } = props
  const { error, validate, defaultValue, getInputProps } = useField(name)
  const { onBlur } = getInputProps()
  const [value, setValue] = useControlField<number | undefined>(name)

  const {
    ref,
    value: maskedValue,
    typedValue,
    setTypedValue,
  } = useIMask(
    // New version of react-imask does not like the typing
    NUMBER_MASK_OPTIONS[maskType] as any
    // defaults(NUMBER_MASK_OPTIONS[maskType], { mask: Number })
  )

  useEffect(() => {
    setValue(typedValue === 0 ? undefined : typedValue)
  }, [maskedValue])

  useEffect(() => {
    // New version takes a typed value, but will leave as is for now
    // This could be helpful to keep if the value is changed elsewhere
    if (value && value != typedValue) {
      setTypedValue(value)
    }
  }, [value])

  return (
    <FieldContainer {...props} id={name}>
      <div className="relative">
        {prefix && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-muted-foreground sm:text-sm">{prefix}</span>
          </div>
        )}
        <input type="hidden" name={name} value={value || ''} />
        <Input
          ref={ref as React.Ref<HTMLInputElement>}
          disabled={disabled}
          className={clsx(
            defaultValue &&
              value != defaultValue &&
              'ring-2 ring-yellow-300 ring-offset-2',
            !defaultValue && !!value && 'ring-2 ring-green-400 ring-offset-2',
            prefix && 'pl-7',
            suffix && 'pr-10'
          )}
          defaultValue={value}
          id={name}
          onBlur={(e) => {
            onBlur?.(e)
            customOnBlur?.(e)
            validate()
          }}
        />
        {suffix && (
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center pr-3">
            <span className="text-muted-foreground sm:text-sm">{suffix}</span>
          </div>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {description && <p className="mt-2 text-sm italic">{description}</p>}
    </FieldContainer>
  )
}
